import { useCallback, useState } from 'react';
import { EResponseHeaders, ESnackbarStyle } from 'shared/types';
import { PAGE_SIZE } from 'shared/constants/pagination';
import { getStudentsAdminList, StudentAdminItem } from 'services/api/studentsAdminService';
import { openNotification } from 'utils/notification-utils';
import { useDebounce } from 'shared/hooks';

type Result = {
  students: StudentAdminItem[];
  fetchStudents: () => Promise<void>;
  totalStudents: number;
  isLoading: boolean;
  currentPage: number;
  setCurrentPage: (page: number) => void;
  sortBy: string;
  setSortBy: (value: string) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  schoolId: string;
  setSchoolId: (value: string) => void;
  dataSource: string;
  setDataSource: (value: string) => void;
  mappingStatus: string;
  setMappingStatus: (value: string) => void;
};

const useStudentsAdminData = (): Result => {
  const [students, setStudents] = useState<StudentAdminItem[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalStudents, setTotalStudents] = useState<number>(0);
  const [sortBy, setSortBy] = useState<string>('');
  const [searchValue, setSearchValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [schoolId, setSchoolId] = useState<string>('');
  const [dataSource, setDataSource] = useState<string>('');
  const [mappingStatus, setMappingStatus] = useState<string>('');

  const debouncedSearch = useDebounce(searchValue, 1000);

  const fetchStudents = useCallback(async (): Promise<void> => {
    setIsLoading(true);
    try {
      const params: any = {};
      if (sortBy) params.orderBy = sortBy;
      if (debouncedSearch) params.search = debouncedSearch;
      if (schoolId) params.schoolId = schoolId;
      if (dataSource) params.dataSource = dataSource;
      if (mappingStatus) params.mappingStatus = mappingStatus;

      const response = await getStudentsAdminList(currentPage, PAGE_SIZE, params);
      setStudents(response.data as any ?? []);
      setTotalStudents(+response.headers[EResponseHeaders.TOTAL_RECORDS] || 0);
      if ((+response.headers[EResponseHeaders.TOTAL_RECORDS] || 0) <= PAGE_SIZE) setCurrentPage(1);
    } catch (e: any) {
      console.error('Students API error:', e);
      openNotification(ESnackbarStyle.ERROR, e.message);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, sortBy, debouncedSearch, schoolId, dataSource, mappingStatus]);

  return {
    students,
    fetchStudents,
    totalStudents,
    isLoading,
    currentPage,
    setCurrentPage,
    sortBy,
    setSortBy,
    searchValue,
    setSearchValue,
    schoolId,
    setSchoolId,
    dataSource,
    setDataSource,
    mappingStatus,
    setMappingStatus,
  };
};

export default useStudentsAdminData;
